import * as TabsPrimitive from "@radix-ui/react-tabs";
import { type ComponentPropsWithoutRef, type ElementRef, forwardRef } from "react";
import { cn } from "../lib/cn";

export const Tabs = TabsPrimitive.Root;

export const TabsList = forwardRef<
  ElementRef<typeof TabsPrimitive.List>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn("flex items-center gap-5 border-b border-border-subtle px-4", className)}
    {...props}
  />
));
TabsList.displayName = "TabsList";

export interface TabsTriggerProps extends ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
  count?: number | string;
  countTone?: "neutral" | "err";
}

/** Underline tabs (Logs / Models / Tests…). Count renders as a small pill. */
export const TabsTrigger = forwardRef<ElementRef<typeof TabsPrimitive.Trigger>, TabsTriggerProps>(
  ({ className, children, count, countTone = "neutral", ...props }, ref) => (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        "-mb-px flex items-center gap-1.5 border-b-2 border-transparent py-2.5 text-base font-medium text-fg-muted transition-colors " +
          "hover:text-fg data-[state=active]:border-accent data-[state=active]:text-fg focus-visible:outline-2 focus-visible:outline-ring",
        className,
      )}
      {...props}
    >
      {children}
      {count !== undefined ? (
        <span
          className={cn(
            "rounded-pill px-1.5 font-mono text-xs leading-4",
            countTone === "err" ? "bg-err text-white" : "bg-surface-inset text-fg-muted",
          )}
        >
          {count}
        </span>
      ) : null}
    </TabsPrimitive.Trigger>
  ),
);
TabsTrigger.displayName = "TabsTrigger";

export const TabsContent = forwardRef<
  ElementRef<typeof TabsPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn("focus-visible:outline-none", className)}
    {...props}
  />
));
TabsContent.displayName = "TabsContent";
